import Route from '@ember/routing/route'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { uniqBy } from 'lodash'
import type SessionService from 'teacher-ui-client/services/session'
import type Store from '@ember-data/store'
import type SchoolClass from 'teacher-ui-client/models/school-class'

export default class AssignmentsRoute extends Route {
  @service session!: SessionService

  @service router!: RouterService

  @service store!: Store

  async model() {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    const isSubco = this.session.currentTeacher.subcoFor(subscriptionType)

    /* Get an array of classes appropriate for the teacher or subco for this subscription type.
     *
     * Note that the assignment table rows have an "Assigned By" column, where we display the name of the teacher that
     * assigned it.
     *
     * The assignments that the current teacher can see, can be assigned by one of;
     * 1) the current teacher
     * 2) a different teacher of the class
     * 3) the subco
     *
     * Because a subco may not necessarily teach a class that the current teacher teaches, it won't be returned in
     * the below query('school-class') call. But the subscription-coordinator.teacher is already fetched as part of
     * the application route model.
     */
    const schoolClassesForSubType = await this.store.query<SchoolClass>('school-class', {
      filter: { product: subscriptionType },
      // @ts-expect-error - include types not correct
      include: 'teachers,students',
    })
    const schoolClasses = schoolClassesForSubType.filter(
      (sc) => isSubco || sc.teachers.includes(this.session.currentTeacher),
    )

    // infer students from the filtered list of classes.
    const students = uniqBy(
      schoolClasses.slice().flatMap((sc) => sc.students?.slice() ?? []),
      'id',
    )

    if (schoolClasses.length === 0 || students.length === 0) {
      void this.router.transitionTo('subscription-type.error-no-students')
      return { schoolClasses, students }
    }

    return { schoolClasses, students }
  }
}
